import { useTranslate } from '@tolgee/react';
import React, { FC, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FolderCard from '../cards/folderCard';
import CreateFolderModal from '../modals/folderModal';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import {
  createTextBlockFolder,
  deleteTextBlockFolder,
  editTextBlockFolder,
  fetchASubFolder,
  getTextBlocksFolder
} from '../../../../../redux/slices/textBlocks';
import EditorTitle from '../../commonElements/editorTitle';
import FolderTopCard from '../cards/folderTopCard';
import { lomavisShowToastr } from '../../../../../utils/ui/toastr';

interface foldersProps {
  currentFocusFolder: string;
  setCurrentFocusFolder: React.Dispatch<React.SetStateAction<string>>;
  nullFolder: boolean;
  setNullFolder: React.Dispatch<React.SetStateAction<boolean>>;
}

const Folders: FC<foldersProps> = ({
  currentFocusFolder,
  setCurrentFocusFolder,
  nullFolder,
  setNullFolder
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();

  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState<Record<string, boolean>>(
    {}
  );

  const { allTextBlockFolders, loadingAllFolders } = useSelector(
    (state: any) => state.textBlocks
  );
  const { lomavisCreatorConfig, locationUuid } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  // Rebuild tree when folders are updated from Redux
  useEffect(() => {
    if (allTextBlockFolders?.length >= 0) {
      const structured = buildFolderTree(allTextBlockFolders);
      setFolderTree(structured);
    }
  }, [allTextBlockFolders]);

  const handleCardClick = async (folder: any) => {
    const isExpanded = expandedFolders.includes(folder.uuid);

    if (isExpanded) {
      setExpandedFolders((prev) => prev.filter((id) => id !== folder.uuid));
    } else {
      setExpandedFolders((prev) => [...prev, folder.uuid]);

      if (!folder.children || folder.children.length === 0) {
        setLoadingFolders((prev) => ({ ...prev, [folder.uuid]: true }));

        try {
          const subFolders = await dispatch(fetchASubFolder(folder.uuid));
          if (subFolders) {
            setFolderTree((prevTree) =>
              attachSubFolders(prevTree, folder.uuid, subFolders)
            );
          }
        } finally {
          setLoadingFolders((prev) => {
            const newState = { ...prev };
            delete newState[folder.uuid];
            return newState;
          });
        }
      }
    }
  };

  const attachSubFolders = (
    tree: any[],
    parentUuid: string,
    subFolders: any[]
  ): any[] => {
    return tree.map((folder) => {
      if (folder.uuid === parentUuid) {
        return {
          ...folder,
          children: subFolders
        };
      } else if (folder.children) {
        return {
          ...folder,
          children: attachSubFolders(folder.children, parentUuid, subFolders)
        };
      }
      return folder;
    });
  };

  const buildFolderTree = (flatFolders: any[]) => {
    const folderMap: { [uuid: string]: any } = {};
    const tree: any[] = [];

    flatFolders?.forEach((folder) => {
      const copy = { ...folder, children: [] }; // ✅ create a shallow copy
      folderMap[folder.uuid] = copy;
    });

    flatFolders?.forEach((folder) => {
      if (folder?.parent) {
        folderMap[folder?.parent]?.children.push(folderMap[folder.uuid]);
      } else {
        tree.push(folderMap[folder.uuid]); // root-level folders
      }
    });

    return tree;
  };

  const handleCreateFolder = ({
    name,
    editUuid,
    folderUuid
  }: {
    name: string;
    editUuid?: string;
    folderUuid?: string;
  }) => {
    if (editUuid) {
      dispatch(
        editTextBlockFolder(
          lomavisCreatorConfig?.location.uuid,
          lomavisCreatorConfig?.cloud?.uuid,
          editUuid,
          {
            name
          },
          () => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.success_folderEdit'),
              'success'
            );
          },

          () => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.err_folderEdit'),
              'error'
            );
          }
        )
      );
    } else {
      dispatch(
        createTextBlockFolder(
          locationUuid,
          lomavisCreatorConfig?.cloud?.uuid,
          {
            name,
            lomavis_connect_folder: false,
            location: locationUuid,
            location_uuid: locationUuid,
            uuid: folderUuid,
            saved_text_blocks_folder: true
          },
          () => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.success_folderCreated'),
              'success'
            );

            // If a subfolder is created, open the parent folder directly
            if (folderUuid) {
              setCurrentFocusFolder(folderUuid);
              // Ensure the parent folder is expanded
              if (!expandedFolders.includes(folderUuid)) {
                setExpandedFolders(prev => [...prev, folderUuid]);

                // Load the parent folder's subfolders to ensure the new subfolder is visible
                dispatch(fetchASubFolder(folderUuid)).then(subFolders => {
                  if (subFolders) {
                    setFolderTree(prevTree =>
                      attachSubFolders(prevTree, folderUuid, subFolders)
                    );
                  }
                });
              }
            }
          },

          () => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.err_folderCreated'),
              'error'
            );
          }
        )
      );
    }
  };

  const RenderFolder = ({
    folder,
    depth = 0
  }: {
    folder: any;
    depth?: number;
  }) => {
    const isExpanded = expandedFolders.includes(folder.uuid);

    return (
      <div className="d-flex flex-column mb-1">
        <FolderCard
          folderName={folder.name}
          active={currentFocusFolder === folder?.uuid}
          folderUuid={folder.uuid}
          handleCardClick={() => {
            setCurrentFocusFolder(folder.uuid);
            handleCardClick(folder);
          }}
          editFolder={({ name }) =>
            handleCreateFolder({ name, editUuid: folder.uuid })
          }
          deleteFolder={() => {
            dispatch(
              deleteTextBlockFolder(
                locationUuid,
                lomavisCreatorConfig?.cloud?.uuid,
                folder.uuid,
                () => {
                  lomavisShowToastr(
                    t('lomavisPostApproval.textBlock.success_folderDeleted'),
                    'success'
                  );
                },

                () => {
                  lomavisShowToastr(
                    t('lomavisPostApproval.textBlock.err_folderDeleted'),
                    'error'
                  );
                }
              )
            );
          }}
          addSubfolder={
            depth < 2
              ? ({ name }) =>
                  handleCreateFolder({ name, folderUuid: folder.uuid })
              : undefined
          }
          loading={loadingFolders[folder.uuid]}
          showFolderAdd={depth < 2 ? true : false}
        />

        {isExpanded && folder.children?.length > 0 && (
          <div className="ps-6 border-start border-1">
            {folder.children.map((child: any) => (
              <RenderFolder key={child.uuid} folder={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="d-flex flex-column gap-2 h-100">
      <div className="d-flex align-items-center justify-content-between mb-1 pt-2">
        <EditorTitle title={'textBlockFolders'} toolTip={false} />
        <i
          onClick={() => setShowModal(true)}
          className="fa-solid fa-plus cursor-pointer fs-3"
        ></i>
      </div>
      <div className="w-100 h-100 mt-3" style={{ overflowY: 'auto' }}>
        <div className="w-100 h-100 d-flex flex-column ">
          <div className="w-100 d-flex flex-column gap-2 mb-4">
            <FolderTopCard
              folderName={t('lomavisCreator.textBlock.all')}
              icon="fa-globe"
              active={currentFocusFolder === '' && !nullFolder}
              handleCardClick={() => {
                setCurrentFocusFolder('');
                setNullFolder(false);
              }}
              loading={loadingFolders['']}
              showFolderAdd={false}
            />
            <FolderTopCard
              folderName={t('lomavisCreator.textBlock.notSavedInFolder')}
              icon="fa-layer-group"
              active={currentFocusFolder === '' && nullFolder}
              isNullFolder={true}
              handleCardClick={() => {
                setCurrentFocusFolder('');
                setNullFolder(true);
              }}
              loading={loadingFolders['']}
              showFolderAdd={false}
            />
          </div>
          {loadingAllFolders
            ? Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={index}
                  className="skeleton-loader w-100 mb-3 rounded"
                  style={{ height: '20px' }}
                ></div>
              ))
            : folderTree.map((folder) => (
                <RenderFolder key={folder.uuid} folder={folder} depth={0} />
              ))}
        </div>
      </div>

      <CreateFolderModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onCreate={handleCreateFolder}
        action="create"
      />
    </div>
  );
};

export default Folders;
